import React from 'react';
import { css, cx } from 'emotion';
import { text, brandAltBackground, neutral, } from '@guardian/src-foundations/palette';
import { headline, textSans } from '@guardian/src-foundations/typography';
import { from } from '@guardian/src-foundations/mq';
import { Pillar } from '@guardian/types';
import ArrowInCircle from '@frontend/static/icons/arrow-in-circle.svg';
import { pillarPalette } from '@frontend/lib/pillars';
import { StarRating } from '@root/src/web/components/StarRating/StarRating';
import { QuoteIcon } from '@root/src/web/components/QuoteIcon';
import { Hide } from '@root/src/web/components/Hide';
import { Avatar } from '@frontend/web/components/Avatar';
import { decidePalette } from '../lib/decidePalette';
const neutralBackground = css `
	background-color: ${neutral[97]};
	a {
		color: inherit;
	}
	:hover {
		background-color: ${neutral[93]};
	}
`;
const richLinkPillarColour = (format) => {
    if (format) {
        return pillarPalette[format.theme].main;
    }
    return pillarPalette[Pillar.News][400];
};
const pillarBackground = (format) => {
    return css `
		background-color: ${richLinkPillarColour(format)};
	`;
};
const textColour = (format) => {
    return css `
		color: ${richLinkPillarColour(format)};
	`;
};
const richLinkTopBorder = (format) => {
    return css `
		border-top: 1px;
		border-top-style: solid;
		border-top-color: ${richLinkPillarColour(format)};
	`;
};
const richLinkLink = css `
	text-decoration: none;
`;
const richLinkElements = css `
	padding-top: 2px;
	padding-right: 5px;
	padding-left: 5px;
	padding-bottom: 5px;
`;
const richLinkHeader = css `
	padding-bottom: 10px;
	color: ${neutral[0]};
`;
const richLinkTitle = css `
	${headline.xxxsmall()};
	font-size: 14px;
	padding-top: 1px;
	padding-bottom: 1px;
	font-weight: 400;
	${from.wide} {
		${headline.xxsmall()};
		padding-bottom: 5px;
	}
`;
const richLinkReadMore = (format) => {
    return css `
		fill: ${richLinkPillarColour(format)};
		color: ${richLinkPillarColour(format)};
		padding-top: 2px;
	`;
};
const readMoreTextStyle = css `
	${headline.xxxsmall()};
	font-size: 14px;
	${from.wide} {
		${headline.xxxsmall()}
		line-height: 25px;
	}
	display: inline-block;
	height: 30px;
	line-height: 25px;
	padding-left: 4px;
	vertical-align: top;
	font-weight: 500;
	text-decoration: none;
`;
const byline = css `
	${headline.xxxsmall()};
	font-size: 14px;
	font-style: italic;
	${from.wide} {
		${headline.xxsmall()};
	}
`;
const contributorImageWrapper = css `
	width: 5rem;
	height: 5rem;
	margin-left: auto;
	margin-right: 0.3rem;
	${from.wide} {
		width: 8.5rem;
		height: 8.5rem;
	}

	/* TODO remove the default img styling in ArticleBody.tsx - do we need direct element styling? */
	img {
		width: 100%;
		height: 100%;
	}
`;
const paidForBranding = css `
	${textSans.xsmall()};
	font-weight: bold;
	color: ${text.supporting};
`;
const starWrapper = css `
	background-color: ${brandAltBackground.primary};
	display: inline-block;
`;
const readMoreText = (contentType) => {
    switch (contentType) {
        case 'audio':
            return 'Listen';
        case 'gallery':
            return 'View Gallery';
        case 'video':
            return 'Watch';
        default:
            return 'Read more';
    }
};
const getMainContributor = (tags) => {
    const contributorTags = tags.filter((t) => t.type === 'Contributor');
    return contributorTags.length > 0 ? contributorTags[0].title : '';
};
const imageStyles = css `
	width: 100%;
	height: auto;
`;
export const RichLink = ({ richLinkIndex, cardStyle, thumbnailUrl, headlineText, contentType, url, starRating, format, tags, sponsorName, contributorImage, isPlaceholder, }) => {
    const linkText = cardStyle === 'letters' ? `${headlineText} | Letters ` : headlineText;
    const imageCardStyles = ['news', 'letters', 'media', 'feature'];
    const showImage = thumbnailUrl && imageCardStyles.includes(cardStyle);
    const isPaidContent = tags
        ? tags.filter((t) => t.id === 'tone/advertisement-features').length > 0
        : false;
    const isOpinion = cardStyle === 'comment';
    const mainContributor = getMainContributor(tags);
    return (React.createElement("div", { "data-print-layout": "hide", "data-link-name": `rich-link-${richLinkIndex} | ${richLinkIndex}`, "data-component": "rich-link", className: pillarBackground(format), "data-name": (isPlaceholder && 'placeholder') || '' },
        React.createElement("div", { className: neutralBackground },
            React.createElement("a", { className: richLinkLink, href: url },
                React.createElement("div", { className: richLinkTopBorder(format) }),
                showImage && (React.createElement("div", null,
                    React.createElement("img", { className: imageStyles, src: thumbnailUrl, alt: "" }))),
                React.createElement("div", { className: richLinkElements },
                    React.createElement("div", { className: richLinkHeader },
                        React.createElement("div", { className: richLinkTitle },
                            isOpinion && (React.createElement(React.Fragment, null,
                                React.createElement(Hide, { when: "above", breakpoint: "wide" },
                                    React.createElement(QuoteIcon, { colour: pillarPalette[format.theme]
                                            .main, size: "small" })),
                                React.createElement(Hide, { when: "below", breakpoint: "wide" },
                                    React.createElement(QuoteIcon, { colour: pillarPalette[format.theme]
                                            .main, size: "medium" })))),
                            linkText),
                        isOpinion && (React.createElement("div", { className: cx(byline, textColour(format)) }, mainContributor)),
                        starRating && starRating > 0 && (React.createElement("div", { className: starWrapper },
                            React.createElement(StarRating, { rating: starRating, size: "medium" }))),
                        isPaidContent && sponsorName && (React.createElement("div", { className: paidForBranding },
                            "Paid for by ",
                            sponsorName))),
                    isOpinion && contributorImage && (React.createElement("div", { className: contributorImageWrapper },
                        React.createElement(Avatar, { imageSrc: contributorImage, imageAlt: mainContributor, palette: decidePalette(format) }))),
                    React.createElement("div", { className: richLinkReadMore(format) },
                        React.createElement(ArrowInCircle, null),
                        React.createElement("div", { className: readMoreTextStyle }, readMoreText(contentType))))))));
};
